import { useMounted } from "@mantine/hooks";
import { type VirtualItem, useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";

import { Box } from "metabase/ui";

import { useEffectOnceIf } from "./use-effect-once-if";

const DEFAULT_END_REACHED_THRESHOLD = 20;

/**
 * Use this when you need to virtualize a 1-dimensional list of items with variable heights.
 * You can override the default wrapper element (just a div) by passing a `Wrapper` prop.
 *
 * It must be inside an element with a fixed height.
 */
export function VirtualizedList({
  children,
  Wrapper = Box,
  estimatedItemSize = 32,
  extraPadding = 0, // sometimes needed to prevent unnecessary scrollbars
  scrollTo,
  onEndReached,
  endReachedThreshold = DEFAULT_END_REACHED_THRESHOLD,
}: {
  children: React.ReactNode[];
  Wrapper?: React.JSXElementConstructor<any>;
  estimatedItemSize?: number;
  extraPadding?: number;
  scrollTo?: number;
  /** Called once when the rendered range scrolls within `endReachedThreshold` items of the end
   * of `children`. Fires again if `children` grows (e.g. after a "load more" append). */
  onEndReached?: () => void;
  endReachedThreshold?: number;
}) {
  const parentRef = useRef(null);
  const isMounted = useMounted();

  const virtualizer = useVirtualizer({
    count: children.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimatedItemSize,
    overscan: 5, // makes unit testing easier
  });

  const items = virtualizer.getVirtualItems();

  useEffectOnceIf(
    () => {
      if (scrollTo && scrollTo < children.length) {
        window.requestAnimationFrame(() => {
          virtualizer.scrollToIndex(scrollTo, { align: "center" });
        });
      }
    },
    isMounted && scrollTo !== undefined && scrollTo !== -1,
  );

  // Guards against firing `onEndReached` repeatedly while parked near the bottom before new
  // items arrive. Resets whenever `children.length` grows, so it can fire again for the next
  // page boundary.
  const firedForLengthRef = useRef<number | null>(null);
  const lastRenderedIndex = items[items.length - 1]?.index;

  useEffect(() => {
    if (
      !onEndReached ||
      children.length === 0 ||
      lastRenderedIndex === undefined
    ) {
      return;
    }
    const isNearEnd =
      lastRenderedIndex >= children.length - 1 - endReachedThreshold;
    if (isNearEnd && firedForLengthRef.current !== children.length) {
      firedForLengthRef.current = children.length;
      onEndReached();
    }
  }, [lastRenderedIndex, children.length, endReachedThreshold, onEndReached]);

  return (
    <div
      ref={parentRef}
      style={{
        height: "100%",
        overflowY: "auto",
      }}
      data-testid="scroll-container"
    >
      <div
        style={{
          height: virtualizer.getTotalSize() + extraPadding,
          position: "relative",
        }}
      >
        <Wrapper
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            transform: `translateY(${items[0]?.start ?? 0}px)`,
            width: "100%",
          }}
        >
          {items.map((virtualRow: VirtualItem) => (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
            >
              {children[virtualRow.index]}
            </div>
          ))}
        </Wrapper>
      </div>
    </div>
  );
}
